import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import VerdictPanel from "@/components/VerdictPanel";
import { toast } from "sonner";

export default function AutorisationNew() {
  const { entreprises, zones, labels } = useApp();
  const [sp] = useSearchParams();
  const opId = sp.get("op");
  const nav = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const ee = entreprises.filter((e) => e.type === "exterieure");

  const [form, setForm] = useState({
    operationId: opId || null, ent: ee[0]?.id || "", zone: zones[0]?.id || "",
    date: today, deb: "08:00", fin: "12:00", typesTravail: [], desc: "", surveillant: "",
  });
  const [op, setOp] = useState(null);
  const [evalRes, setEvalRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (opId) api.get(`/operations/${opId}`).then((r) => {
      setOp(r.data);
      setForm((f) => ({ ...f, ent: r.data.eeId, zone: r.data.zones[0] || f.zone, natureTravaux: r.data.natureTravaux }));
    });
  }, [opId]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/autorisations/evaluate", {
        zone: form.zone, date: form.date, deb: form.deb, fin: form.fin,
        typesTravail: form.typesTravail, surveillant: form.surveillant, permis: [],
      }).then((r) => setEvalRes(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [form.zone, form.date, form.deb, form.fin, form.typesTravail, form.surveillant]);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (v) => setForm((f) => ({ ...f, typesTravail: f.typesTravail.includes(v) ? f.typesTravail.filter((x) => x !== v) : [...f.typesTravail, v] }));
  const zoneOpts = useMemo(() => (op ? zones.filter((z) => z.siteId === op.siteId) : zones), [zones, op]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/autorisations", { ...form });
      toast.success(`${data.id} soumise`);
      nav(`/autorisations/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="crumb">Autorisation de travail · brouillon{op ? ` · ${op.titre}` : ""}</div>
      <h1>Nouvelle autorisation</h1>
      <div className="grid2" style={{ gridTemplateColumns: "1.2fr 1fr", alignItems: "start", marginTop: 16 }}>
        <div className="card">
          <label className="f">Entreprise intervenante</label>
          <select data-testid="auto-ent" value={form.ent} onChange={(e) => up("ent", e.target.value)}>
            {ee.map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
          </select>
          <label className="f">Zone d'intervention</label>
          <select data-testid="auto-zone" value={form.zone} onChange={(e) => up("zone", e.target.value)}>
            {zoneOpts.map((z) => <option key={z.id} value={z.id}>{z.nom}{z.attributs.length ? " · " + z.attributs.map((a) => labels.attrs[a]).join(", ") : ""}</option>)}
          </select>
          <div className="row3">
            <div><label className="f">Date</label><input type="date" data-testid="auto-date" value={form.date} onChange={(e) => up("date", e.target.value)} /></div>
            <div><label className="f">Début</label><input type="time" data-testid="auto-deb" value={form.deb} onChange={(e) => up("deb", e.target.value)} /></div>
            <div><label className="f">Fin</label><input type="time" data-testid="auto-fin" value={form.fin} onChange={(e) => up("fin", e.target.value)} /></div>
          </div>
          <label className="f">Types de travaux</label>
          <div className="chips">
            {Object.entries(labels.travaux).map(([k, v]) => (
              <button type="button" key={k} className={`chip ${form.typesTravail.includes(k) ? "on" : ""}`} onClick={() => toggle(k)} data-testid={`auto-travail-${k}`}>{v}</button>
            ))}
          </div>
          <label className="f">Description</label>
          <textarea rows={2} data-testid="auto-desc" value={form.desc} onChange={(e) => up("desc", e.target.value)} />
          <label className="f">Surveillant extérieur (espace confiné)</label>
          <input data-testid="auto-surveillant" value={form.surveillant} onChange={(e) => up("surveillant", e.target.value)} placeholder="Nom du surveillant désigné" />
          <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
            <button className="btn btn-p" data-testid="auto-submit" onClick={submit} disabled={!form.typesTravail.length || busy}>Soumettre la demande</button>
          </div>
        </div>
        <VerdictPanel evaluation={evalRes} title={evalRes && !evalRes.items.length ? "Aucun blocage détecté" : undefined} />
      </div>
    </div>
  );
}
